import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { formatTime, getTimeFromString } from './dateUtils';
import { DEFAULT_LANGUAGE, getFieldOnMainLanguage } from './languages';
import { makeCommaSeparatedString } from './stringUtils';

export interface Row {
  id: number;
  start_time: string;
  end_time: string;
  segment: GenericObject | undefined;
  series: GenericObject | undefined;
  guests: string;
  experts: GenericObject[];
  fromBackend: boolean;
  is_deleted?: boolean;
  date_created?: string;
}
export interface ErrorInterface {
  key: string;
  id: number;
}

export interface ContextMenuProps {
  rowId: number;
  rowIdx: number;
  top: number;
  left: number;
}

export const getCellClass = (key: string, row: Row, errors: ErrorInterface[]) => {
  return errors.find((error) => error.key === key && error.id === row.id) && !row[key as keyof Row] ? 'cell-error' : '';
};

export const addNewSegment = (oldRows: Row[]) => {
  const lastId = oldRows.length ? oldRows[oldRows.length - 1]?.id + 1 : 1;
  return [
    ...oldRows,
    {
      id: lastId,
      start_time: '',
      end_time: '',
      segment: undefined,
      series: undefined,
      guests: '',
      experts: [],
      fromBackend: false,
    },
  ];
};

export const getEpgErrors = (rows: Row[], columns: GenericObject[]) => {
  const newErrors: ErrorInterface[] = [];
  rows.forEach((row) => {
    if (row.start_time || row.end_time || row.segment || row.series || row.guests || row.experts.length) {
      Object.keys(row).forEach((key: string) => {
        const column = columns.find((column) => column.key === key);
        if (column?.required && !row[key as keyof Row]) {
          newErrors.push({ key, id: row.id });
        }
      });
    }
  });
  return newErrors;
};

export const rowKeyGetter = (row: Row) => {
  return row.id;
};

export const getExpertNames = (row: Row) => {
  const expertNames = row.experts.map(
    (expert) => `${getFieldOnMainLanguage(expert.first_name)} ${getFieldOnMainLanguage(expert.last_name)}`
  );

  return expertNames;
};

export const getEpgPayload = (rows: Row[], date: string | null) => {
  const validRows = rows.filter((row) => row.start_time && row.end_time && row.segment && row.series);

  return validRows.map((row) => {
    const expertNames = getExpertNames(row);

    const end = getTimeFromString(date, row.end_time);
    const start = getTimeFromString(date, row.start_time);

    const payload: GenericObject = {
      expert_ids: row.experts.map((expert) => expert.id),
      experts: { [DEFAULT_LANGUAGE]: makeCommaSeparatedString(expertNames) },
      start_date_time: start,
      end_date_time: end,
      guest: { [DEFAULT_LANGUAGE]: row.guests },
      segment_id: row.segment?.id,
      event_series_id: row.series?.id,
    };

    if (row.fromBackend) {
      payload.id = row.id;
      payload.date_created = row.date_created;
    }

    if (Object.hasOwn(row, 'is_deleted')) {
      payload.is_deleted = row.is_deleted;
    }

    return payload;
  });
};

export const formatBackendResponse = (data: GenericObject) => {
  return {
    id: data.id,
    start_time: formatTime(data.start_date_time as string),
    end_time: formatTime(data.end_date_time as string),
    segment: data.segment,
    series: data.event_series,
    guests: (data.guest as GenericObject)[DEFAULT_LANGUAGE],
    experts: data.expert_list,
    date_created: data.date_created,
    is_deleted: data.is_deleted,
    fromBackend: true,
  };
};
